import { useEffect, useRef } from "react";
import { gsap } from "gsap";

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Reveal children marked `.reveal` inside the container with a soft stagger. */
export function useReveal<T extends HTMLElement = HTMLDivElement>(deps: any[] = []) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll<HTMLElement>(".reveal");
    if (reduced()) { gsap.set(targets, { opacity: 1, y: 0 }); return; }
    const ctx = gsap.context(() => {
      gsap.fromTo(targets,
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", stagger: 0.06, clearProps: "transform" });
    }, el);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

/** Magnetic hover — element leans toward the cursor. Use on buttons / cards. */
export function useMagnetic<T extends HTMLElement = HTMLElement>(strength = 0.35) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced()) return;
    const qx = gsap.quickTo(el, "x", { duration: 0.5, ease: "power3.out" });
    const qy = gsap.quickTo(el, "y", { duration: 0.5, ease: "power3.out" });
    function move(e: MouseEvent) {
      const r = el!.getBoundingClientRect();
      qx((e.clientX - (r.left + r.width / 2)) * strength);
      qy((e.clientY - (r.top + r.height / 2)) * strength);
    }
    function leave() { qx(0); qy(0); }
    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    return () => { el.removeEventListener("mousemove", move); el.removeEventListener("mouseleave", leave); };
  }, [strength]);
  return ref;
}

/** Animate a number from 0 → value. Returns a ref to attach to a text node. */
export function useCountUp(value: number, format: (n: number) => string = (n) => `${Math.round(n)}`) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduced()) { el.textContent = format(value); return; }
    const obj = { n: 0 };
    const tw = gsap.to(obj, { n: value, duration: 1.1, ease: "power2.out", onUpdate: () => { el.textContent = format(obj.n); } });
    return () => { tw.kill(); };
  }, [value]);
  return ref;
}

export { gsap };
